import { useState, useEffect, useRef, useMemo } from "react";
import { Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";
import { t, font, Avatar, GlobalStyles } from "@realtyflows/ui";
import { getMe, hasToken, removeToken, getGmailStatus, getClients, getUnreadCount, getSiteTheme, getBillingStatus, getFeatureFlag } from "./api";
import Sidebar from "./components/Sidebar";
import TwoRailSidebar from "./components/TwoRailSidebar";
import Dashboard from "./pages/Dashboard";
import Clients from "./pages/Clients";
import ClientDetail from "./pages/ClientDetail";
import Pipeline from "./pages/Pipeline";
import Messages from "./pages/Messages";
import Appointments from "./pages/Appointments";
// Automations is now embedded within Settings
import Settings from "./pages/Settings";
import Pricing from "./pages/Pricing";
import Team from "./pages/Team";
import AgentView from "./pages/AgentView";
import Home from "./pages/Home";
import Login from "./pages/Login";
import Emails from "./pages/Emails";
import Signup from "./pages/Signup";
import Terms from "./pages/Terms";
import Privacy from "./pages/Privacy";
import Contact from "./pages/Contact";
import Billing from "./pages/Billing";
import Contracts from "./pages/Contracts";
import TemplatePreview from "./pages/TemplatePreview";
import FormEditor from "./pages/FormEditor";
import FolderDetail from "./pages/FolderDetail";
import ESignPrepare from "./pages/ESignPrepare";
import TemplateEdit from "./pages/TemplateEdit";
import TemplateSettings from "./pages/TemplateSettings";

import PacketList from "./pages/PacketList";
import PacketBuilder from "./pages/PacketBuilder";
import PacketFiller from "./pages/PacketFiller";
import CreateDeal from "./pages/CreateDeal";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import VerifyEmail from "./pages/VerifyEmail";
import Footer from "./components/Footer";
import ChatWidget from "./components/ChatWidget";
import AIAssistant from "./components/AIAssistant";
import useResponsive from "./hooks/useResponsive";
import ConfirmModal from "./components/ConfirmModal";

const tabLabels = {
  "/dashboard": "Dashboard",
  "/contracts": "Contracts",
  "/contracts/templates": "Contract Templates",
  "/packets": "Transaction Packets",
  "/clients": "Clients",
  "/deals": "Deals",
  // "/messages": "Messages",
  "/emails": "Emails",
  "/appointments": "Appointments",
  "/settings": "Settings",
  "/billing": "Billing",
  "/pricing": "Upgrade Plan",
  "/team": "Team Management",
  "/terms": "Terms of Service",
  "/privacy": "Privacy Policy",
  "/contact": "Contact Us",
};

export default function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [emailUnread, setEmailUnread] = useState(0);
  const [pendingForm, setPendingForm] = useState(null);
  const [trialInfo, setTrialInfo] = useState(null);
  // Workspace: null = personal, teamId = team context
  const [activeWorkspace, setActiveWorkspace] = useState(() => {
    try { return localStorage.getItem("rf_active_workspace") || null; } catch { return null; }
  });
  const [workspaceTransition, setWorkspaceTransition] = useState(false);
  const [siteTheme, setSiteTheme] = useState(() => {
    // Initialize from cached value so first render uses correct theme
    try { return localStorage.getItem("rf_site_theme") || "classic"; } catch { return "classic"; }
  });
  const [chatbotEnabled, setChatbotEnabled] = useState(false);
  const [showAIAssistant, setShowAIAssistant] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const mainRef = useRef(null);

  // Set global theme flag for @realtyflows/ui tokens
  window.__RF_THEME = siteTheme;

  useEffect(() => {
    if (hasToken()) {
      getMe()
        .then(u => setUser(u))
        .catch(() => removeToken())
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
    getSiteTheme().then(theme => {
      setSiteTheme(theme);
      window.__RF_THEME = theme;
      try { localStorage.setItem("rf_site_theme", theme); } catch {}
    });
    getFeatureFlag("chatbot_enabled").then(setChatbotEnabled).catch(() => {});
  }, []);

  // Fetch billing status
  const refreshBilling = () => {
    if (!user) return;
    getBillingStatus().then(b => setTrialInfo(b)).catch(() => {});
  };
  useEffect(() => { refreshBilling(); }, [user]);

  // Fetch unread email count from clients only (polls every 2 minutes)
  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    const fetchUnread = async () => {
      try {
        const status = await getGmailStatus();
        if (!status.connected) return;
        // Skip unread count if user only has send scope (no read access)
        const hasRead = (status.scopes || []).some(s => s.includes("readonly") || s.includes("modify"));
        if (!hasRead) return;
        const clients = await getClients();
        const emails = clients.map(c => c.email).filter(Boolean);
        if (emails.length === 0) return;
        // Gmail query: unread emails from any client email
        const query = `is:unread {${emails.map(e => `from:${e}`).join(" ")}}`;
        const { count } = await getUnreadCount(query);
        if (!cancelled) setEmailUnread(count || 0);
      } catch (e) { /* gmail not connected or no clients */ }
    };
    fetchUnread();
    const interval = setInterval(fetchUnread, 120000);
    return () => { cancelled = true; clearInterval(interval); };
  }, [user]);

  // Scroll main content to top on route change
  useEffect(() => {
    if (mainRef.current) mainRef.current.scrollTop = 0;
    window.scrollTo(0, 0);
  }, [location.pathname]);

  const responsive = useResponsive();

  const handleWorkspaceChange = (workspaceId) => {
    if (workspaceId === activeWorkspace) return;
    // Trigger transition: fade out content
    setWorkspaceTransition(true);
    setTimeout(() => {
      setActiveWorkspace(workspaceId);
      try {
        if (workspaceId) localStorage.setItem("rf_active_workspace", workspaceId);
        else localStorage.removeItem("rf_active_workspace");
      } catch {}
      // Fade content back in after data context switches
      setTimeout(() => setWorkspaceTransition(false), 80);
    }, 250);
  };

  const handleLogin = (userData) => {
    setUser(userData);
    navigate("/dashboard");
  };

  const handlePendingVerification = (formData) => {
    setPendingForm(formData);
    try { localStorage.setItem("rf_pending_signup", JSON.stringify(formData)); } catch {}
    navigate("/verify-email");
  };

  const handleVerified = (userData) => {
    setPendingForm(null);
    try { localStorage.removeItem("rf_pending_signup"); } catch {}
    setUser(userData);
    navigate("/dashboard");
  };

  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);

  const handleLogout = () => {
    setShowLogoutConfirm(true);
  };

  const confirmLogout = () => {
    setShowLogoutConfirm(false);
    removeToken();
    setUser(null);
    navigate("/");
  };

  if (loading) {
    return (
      <><GlobalStyles /><div style={{ minHeight: "100vh", background: t.pageBg, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <div style={{ textAlign: "center" }}>
          <img src="/logo.png" alt="RealtyFlows" style={{ width: 32, height: 32, borderRadius: 5, marginBottom: 12 }} />
          <div style={{ fontSize: 14, color: t.textMuted, fontFamily: font.body }}>Loading...</div>
        </div>
      </div></>
    );
  }

  // Public routes
  if (!user) {
    return (
      <><GlobalStyles /><div style={{ minHeight: "100vh", background: t.pageBg, color: t.textPrimary, fontFamily: font.body, display: "flex", flexDirection: "column" }}>
        <div style={{ flex: 1 }}>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/login" element={<Login onLogin={handleLogin} />} />
            <Route path="/signup" element={<Signup onSignup={handleLogin} onPendingVerification={handlePendingVerification} />} />
            <Route path="/verify-email" element={<VerifyEmail pendingForm={pendingForm} onVerified={handleVerified} />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </div>
        {location.pathname !== "/" && <Footer />}
        {chatbotEnabled && <ChatWidget />}
      </div></>
    );
  }

  // Standalone pages (no sidebar/header) even when logged in
  const standalonePaths = ["/terms", "/privacy", "/contact", "/forgot-password", "/reset-password"];
  if (standalonePaths.includes(location.pathname)) {
    return (
      <><GlobalStyles /><div style={{ minHeight: "100vh", background: t.pageBg, color: t.textPrimary, fontFamily: font.body, display: "flex", flexDirection: "column" }}>
        <div style={{ flex: 1 }}>
          <Routes>
            <Route path="/terms" element={<Terms embedded />} />
            <Route path="/privacy" element={<Privacy embedded />} />
            <Route path="/contact" element={<Contact embedded user={user} />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
          </Routes>
        </div>
        <Footer />
        {chatbotEnabled && <ChatWidget />}
      </div></>
    );
  }

  // Get page title from path
  const currentPath = "/" + location.pathname.split("/")[1];
  const pageTitle = location.pathname === "/clients/detail"
    ? "Client Details"
    : location.pathname.startsWith("/team/agent/")
    ? "Agent Dashboard"
    : tabLabels[currentPath] || "Dashboard";

  const isTwoRail = siteTheme === "two-rail";
  const mainMarginLeft = isTwoRail ? 256 : undefined;

  return (
    <><GlobalStyles /><div style={{ height: "100%", background: t.pageBg, color: t.textPrimary, fontFamily: font.body, display: "flex", overflow: "hidden" }}>
        {isTwoRail ? (
          <TwoRailSidebar
            user={user}
            plan={user.plan}
            onLogout={handleLogout}
            emailUnread={emailUnread}
            activeWorkspace={activeWorkspace}
            onWorkspaceChange={handleWorkspaceChange}
          />
        ) : (
          <Sidebar user={user} plan={user.plan} role={user.role} onLogout={handleLogout} collapsed={location.pathname === "/emails" || sidebarCollapsed} onToggle={() => setSidebarCollapsed(c => !c)} hideToggle={location.pathname === "/emails"} emailUnread={emailUnread} trialInfo={trialInfo} activeWorkspace={activeWorkspace} onWorkspaceChange={handleWorkspaceChange} />
        )}

        <div style={{ flex: 1, minHeight: 0, overflow: "hidden", display: "flex", flexDirection: "column", ...(isTwoRail ? { marginLeft: mainMarginLeft } : {}) }}>
          {/* Header — fixed at top */}
          <div style={{
            padding: responsive.isCompact ? "14px 16px" : "20px 28px", borderBottom: `1.5px solid ${t.border}`,
            display: "flex", alignItems: "center", justifyContent: "space-between",
            background: t.surface, zIndex: 10, flexShrink: 0,
          }}>
            <div>
              <div style={{ fontSize: 20, fontWeight: 500, color: t.textPrimary, letterSpacing: -0.5, fontFamily: font.display }}>
                {pageTitle}
              </div>
              <div style={{ fontSize: 12, color: t.textMuted, fontFamily: font.mono }}>
                {new Date().toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric", year: "numeric" })}
              </div>
            </div>

            {/* Workspace Switcher — sliding pill */}
            {user.team_id && (
              <div style={{ position: "relative", display: "inline-flex", alignItems: "center", background: t.pageBg, borderRadius: 99, padding: 3, border: `1px solid ${t.border}`, overflow: "hidden" }}>
                {/* Sliding background indicator */}
                <div style={{
                  position: "absolute", top: 3, bottom: 3, width: "calc(50% - 3px)", borderRadius: 99,
                  background: activeWorkspace ? t.emerald : t.navy,
                  transform: activeWorkspace ? "translateX(100%)" : "translateX(0)",
                  transition: "transform 0.35s cubic-bezier(0.4, 0, 0.2, 1), background 0.35s ease",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                  left: 3,
                }} />
                <button onClick={() => handleWorkspaceChange(null)} style={{
                  position: "relative", zIndex: 1, padding: "7px 20px", borderRadius: 99, border: "none", cursor: "pointer",
                  fontSize: 12, fontWeight: 600, fontFamily: font.body,
                  background: "transparent",
                  color: !activeWorkspace ? "#fff" : t.textMuted,
                  transition: "color 0.3s ease",
                }}>Personal</button>
                <button onClick={() => handleWorkspaceChange(String(user.team_id))} style={{
                  position: "relative", zIndex: 1, padding: "7px 20px", borderRadius: 99, border: "none", cursor: "pointer",
                  fontSize: 12, fontWeight: 600, fontFamily: font.body,
                  background: "transparent",
                  color: activeWorkspace ? "#fff" : t.textMuted,
                  transition: "color 0.3s ease",
                }}>Team</button>
              </div>
            )}

            <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
              <div style={{
                display: "flex", alignItems: "center", gap: 6,
                background: t.navyLight,
                padding: "5px 12px",
              }}>
                <div style={{ width: 7, height: 7, borderRadius: "50%", background: "#22c55e", boxShadow: "0 0 6px #22c55e80", animation: "pulse 2s infinite" }} />
                <span style={{ fontSize: 10, color: t.goldLight, fontWeight: 700, fontFamily: font.body, letterSpacing: "1px" }}>AI ACTIVE</span>
              </div>
              <div onClick={() => navigate("/settings")} style={{ cursor: "pointer" }}>
                {user.photo_url ? (
                  <img src={user.photo_url} alt="" style={{ width: 32, height: 32, borderRadius: "50%", objectFit: "cover" }} />
                ) : (
                  <Avatar name={user.name} size={32} />
                )}
              </div>
            </div>
          </div>

          {/* Workspace context bar — shows which workspace is active */}
          {user.team_id && activeWorkspace && (
            <div style={{
              padding: "6px 28px", display: "flex", alignItems: "center", gap: 8,
              background: `${t.emerald}0C`, borderBottom: `1px solid ${t.emerald}20`,
              transition: "all 0.3s ease",
              animation: "slideDown 0.25s ease",
            }}>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={t.emerald} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" />
              </svg>
              <span style={{ fontSize: 11, fontWeight: 600, color: t.emerald, fontFamily: font.body }}>Team Workspace</span>
              <span style={{ fontSize: 11, color: t.textMuted, fontFamily: font.body }}>&middot; Showing team clients only</span>
            </div>
          )}

          {/* Scrollable content */}
          <div ref={mainRef} data-main-content style={{ flex: 1, minHeight: 0, overflowY: "auto", overflowX: "hidden" }}>
          <div style={{
            flex: "1 0 auto", padding: location.pathname === "/emails" ? 0 : responsive.contentPadding,
            opacity: workspaceTransition ? 0 : 1,
            transform: workspaceTransition ? "scale(0.98) translateY(6px)" : "scale(1) translateY(0)",
            transition: "opacity 0.25s ease, transform 0.25s ease",
          }}>
            <Routes>
              <Route path="/dashboard" element={<Dashboard workspace={activeWorkspace} />} />
              <Route path="/contracts" element={<Contracts user={user} workspace={activeWorkspace} />} />
              <Route path="/contracts/folders/:folderId" element={<FolderDetail user={user} />} />
              <Route path="/contracts/folders/:folderId/prepare/:formEntryId" element={<ESignPrepare user={user} />} />
              <Route path="/contracts/templates" element={<Contracts defaultView="templates" user={user} workspace={activeWorkspace} />} />
              <Route path="/contracts/templates/:id" element={<FormEditor user={user} />} />
              <Route path="/contracts/templates/:id/preview" element={<TemplatePreview user={user} />} />
              <Route path="/contracts/templates/:id/edit" element={<TemplateEdit />} />
              <Route path="/contracts/templates/:id/settings" element={<TemplateSettings />} />
              <Route path="/packets" element={<PacketList user={user} workspace={activeWorkspace} />} />
              <Route path="/packets/:id" element={<PacketBuilder user={user} />} />
              <Route path="/packets/:id/fill" element={<PacketFiller user={user} />} />
              <Route path="/clients" element={<Clients workspace={activeWorkspace} />} />
              <Route path="/clients/detail" element={<ClientDetail workspace={activeWorkspace} />} />
              <Route path="/deals" element={<Pipeline workspace={activeWorkspace} />} />
              <Route path="/deals/new" element={<CreateDeal />} />
              {/* <Route path="/messages" element={<Messages />} /> */}
              <Route path="/emails" element={<Emails />} />
              <Route path="/appointments" element={<Appointments />} />
              <Route path="/settings" element={<Settings user={user} onUserUpdate={setUser} onBillingChange={refreshBilling} />} />
              <Route path="/settings/automations" element={<Settings defaultTab="automations" user={user} onUserUpdate={setUser} />} />
              <Route path="/deals/pipelines" element={<Settings defaultTab="pipelines" user={user} onUserUpdate={setUser} />} />
              <Route path="/team" element={<Team user={user} onUserUpdate={setUser} />} />
              <Route path="/team/agent/:agentId" element={<AgentView />} />
              <Route path="/billing" element={<Billing user={user} />} />
              <Route path="/pricing" element={<Navigate to="/settings" replace />} />
              <Route path="*" element={<Navigate to="/dashboard" replace />} />
            </Routes>
          </div>
          </div>{/* end scrollable content */}
        </div>
      {showLogoutConfirm && (
        <ConfirmModal
          title="Sign Out"
          message="Are you sure you want to sign out?"
          confirmLabel="Sign Out"
          onCancel={() => setShowLogoutConfirm(false)}
          onConfirm={confirmLogout}
        />
      )}
      {chatbotEnabled && !showAIAssistant && (
        <button onClick={() => setShowAIAssistant(true)} style={{
          position: "fixed", bottom: 24, right: 24, zIndex: 999,
          width: 56, height: 56, borderRadius: "50%", border: "none", cursor: "pointer",
          background: "linear-gradient(135deg, #1A3260, #2D5AA0)",
          display: "flex", alignItems: "center", justifyContent: "center",
          boxShadow: "0 4px 24px rgba(26,50,96,0.35)",
          transition: "transform 0.2s",
        }}
          onMouseEnter={e => e.currentTarget.style.transform = "scale(1.1)"}
          onMouseLeave={e => e.currentTarget.style.transform = "scale(1)"}
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
          </svg>
        </button>
      )}
      {chatbotEnabled && showAIAssistant && (
        <AIAssistant token={localStorage.getItem("realtyflows_token")} onClose={() => setShowAIAssistant(false)} />
      )}
    </div></>
  );
}
